import { useState } from 'react';
import { log } from '../lib/logger';

export default function AuthDebug() {
  const [status, setStatus] = useState<'idle' | 'validating' | 'redirecting' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [extractedTokens, setExtractedTokens] = useState<{
    access_token: boolean;
    refresh_token: boolean;
    type: string;
    token_hash: boolean;
  } | null>(null);

  function usePaste(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('validating');
    setErrorMsg('');
    setExtractedTokens(null);

    const txt = new FormData(e.currentTarget).get('u') as string;
    if (!txt || !txt.trim()) {
      setErrorMsg('Please paste a recovery link from your email.');
      setStatus('error');
      return;
    }

    const u = txt.trim();
    log.debug('[AUTH-DEBUG] Processing input:', u.substring(0, 50) + '...');

    const url = (() => { try { return new URL(u); } catch { return null; } })();
    let access_token = '', refresh_token = '', type = '', token_hash = '', token = '';

    const collect = (s: URLSearchParams) => {
      access_token ||= s.get('access_token') || '';
      refresh_token ||= s.get('refresh_token') || '';
      type ||= s.get('type') || '';
      token_hash ||= s.get('token_hash') || '';
      token ||= s.get('token') || '';
    };

    if (url) {
      if (url.hash?.startsWith('#')) collect(new URLSearchParams(url.hash.slice(1)));
      collect(new URLSearchParams(url.search));
    } else {
      const frag = u.includes('#') ? u.split('#',2)[1] : '';
      const raw = frag || (u.split('?')[1] || '');
      collect(new URLSearchParams(raw));
    }

    log.debug('[AUTH-DEBUG] Extracted tokens:', {
      has_access_token: !!access_token,
      has_refresh_token: !!refresh_token,
      has_token: !!token,
      type,
      has_token_hash: !!token_hash
    });

    setExtractedTokens({
      access_token: !!access_token,
      refresh_token: !!refresh_token,
      type,
      token_hash: !!token_hash || !!token
    });

    if (!token && !token_hash && (!access_token || !refresh_token)) {
      setErrorMsg('Invalid recovery link. Missing required authentication tokens. Please ensure you copied the complete link from your email.');
      setStatus('error');
      return;
    }

    if ((token || token_hash) && !type) {
      type = 'recovery';
    }

    sessionStorage.setItem('reset_tokens', JSON.stringify({
      access_token,
      refresh_token,
      type,
      token_hash,
      token,
      ts: Date.now()
    }));

    log.debug('[AUTH-DEBUG] Tokens saved to sessionStorage, redirecting...');
    setStatus('redirecting');

    setTimeout(() => {
      window.location.replace('/auth/callback?t=debug');
    }, 800);
  }
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #f8fafc, #f1f5f9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        padding: '2rem',
        width: '100%',
        maxWidth: '600px'
      }}>
        <form onSubmit={usePaste} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0, marginBottom: '0.5rem' }}>Password Recovery Helper</h1>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Paste the complete recovery link from your email below.</p>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
              Recovery Link
            </label>
            <textarea
              name="u"
              rows={6}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                resize: 'vertical',
                outline: 'none'
              }}
              placeholder="https://your-app.com/#access_token=...&refresh_token=...&type=recovery

OR

https://your-app.com/?token_hash=..."
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              disabled={status === 'validating' || status === 'redirecting'}
            />
          </div>

          {extractedTokens && (
            <div style={{
              padding: '0.75rem',
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '0.5rem',
              fontSize: '0.875rem'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#166534' }}>Tokens detected:</div>
              <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#15803d' }}>
                {extractedTokens.token_hash && <li>Recovery token hash found</li>}
                {extractedTokens.access_token && <li>Access token found</li>}
                {extractedTokens.refresh_token && <li>Refresh token found</li>}
                {extractedTokens.type && <li>Type: {extractedTokens.type}</li>}
              </ul>
            </div>
          )}

          {status === 'error' && errorMsg && (
            <div style={{
              padding: '0.75rem',
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              color: '#dc2626'
            }}>
              {errorMsg}
            </div>
          )}

          {status === 'redirecting' && (
            <div style={{
              padding: '0.75rem',
              background: '#eff6ff',
              border: '1px solid #93c5fd',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              color: '#1e40af',
              textAlign: 'center'
            }}>
              Tokens validated. Redirecting to password reset...
            </div>
          )}

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '0.75rem',
              background: status === 'validating' || status === 'redirecting' ? '#9ca3af' : '#111827',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: status === 'validating' || status === 'redirecting' ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s'
            }}
            disabled={status === 'validating' || status === 'redirecting'}
            onMouseEnter={(e) => {
              if (status === 'idle' || status === 'error') {
                e.currentTarget.style.background = '#1f2937';
              }
            }}
            onMouseLeave={(e) => {
              if (status === 'idle' || status === 'error') {
                e.currentTarget.style.background = '#111827';
              }
            }}
          >
            {status === 'validating' && 'Validating...'}
            {status === 'redirecting' && 'Redirecting...'}
            {(status === 'idle' || status === 'error') && 'Process Recovery Link'}
          </button>

          <div style={{
            padding: '0.75rem',
            background: '#f9fafb',
            borderRadius: '0.5rem',
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>
            <strong style={{ color: '#374151' }}>Tip:</strong> Open your password reset email and copy the entire link.
            It should start with your app's URL and contain authentication tokens.
          </div>
        </form>
      </div>
    </div>
  );
}
